import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, ArrowLeft, Layers, Minus, Plus, RotateCcw, ShieldCheck, ShoppingBasket, Truck } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { fmtGNF, unitPriceFor } from "@/lib/format";
import { useCart } from "@/context/CartContext";
import { ProductCard } from "@/components/ProductCard";
import { ProductReviews, Stars } from "@/components/ProductReviews";
import { Loading } from "@/components/ProtectedRoute";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [data, setData] = useState(null);
  const [qty, setQty] = useState(1);
  const [active, setActive] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setQty(1);
    setActive(0);
    api
      .get(`/products/${id}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Produit introuvable"))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <Loading label="Chargement du produit…" />;
  if (!data)
    return (
      <div className="mx-auto max-w-lg px-4 py-20 text-center">
        <p className="font-display text-xl font-bold">Produit introuvable</p>
        <Link to="/catalogue" className="ds-btn-primary mt-4">Retour au catalogue</Link>
      </div>
    );

  const p = data.product;
  const images = (p.images || []).filter(Boolean);
  const unit = unitPriceFor(p, qty);
  const wholesaleActive = unit !== Number(p.price);
  const low = p.stock > 0 && p.stock <= (p.low_stock_threshold || 0);
  const promo = p.compare_price && p.compare_price > p.price;

  const add = (go = false) => {
    addItem(p, qty);
    toast.success(`${qty} × ${p.name} ajouté au panier`);
    if (go) navigate("/panier");
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6" data-testid="product-detail-page">
      <nav className="flex flex-wrap items-center gap-1 text-xs text-slate-500" data-testid="product-breadcrumb">
        <Link to="/" className="hover:text-brand">Accueil</Link>
        <span>›</span>
        <Link to={`/catalogue?category=${p.category_id}`} className="hover:text-brand">{p.category_name}</Link>
        <span>›</span>
        <span className="line-clamp-1 font-semibold text-slate-700">{p.name}</span>
      </nav>

      <button type="button" onClick={() => navigate(-1)} className="mb-4 mt-2 flex items-center gap-1 text-sm text-slate-500 hover:text-brand">
        <ArrowLeft className="h-4 w-4" /> Retour
      </button>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,420px)_1fr_300px]">
        <div>
          <div className="ds-card aspect-square overflow-hidden bg-slate-100">
            {images[active] ? (
              <img src={mediaUrl(images[active])} alt={p.name} className="h-full w-full object-cover" />
            ) : (
              <div className="grid h-full place-items-center text-slate-300">
                <ShoppingBasket className="h-16 w-16" />
              </div>
            )}
          </div>
          {images.length > 1 && (
            <div className="mt-3 flex gap-2 overflow-x-auto no-scrollbar">
              {images.map((img, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setActive(i)}
                  className={`h-16 w-16 shrink-0 overflow-hidden rounded-xl border-2 ${i === active ? "border-brand" : "border-slate-200"}`}
                  data-testid={`product-thumb-${i}`}
                >
                  <img src={mediaUrl(img)} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-brand">{p.category_name}</p>
          <h1 className="mt-1 font-display text-2xl font-extrabold text-slate-900 sm:text-3xl" data-testid="product-title">
            {p.name}
          </h1>
          <p className="font-num mt-1 text-xs text-slate-500">Référence : {p.sku}</p>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <p className="font-num text-2xl font-black text-brand sm:text-3xl" data-testid="product-price">
              {fmtGNF(unit)}
            </p>
            {promo && <p className="font-num text-sm text-slate-400 line-through">{fmtGNF(p.compare_price)}</p>}
            {wholesaleActive && (
              <span className="ds-chip border-gold bg-gold-light text-gold-dark">
                <Layers className="h-3.5 w-3.5" /> Prix de gros appliqué
              </span>
            )}
          </div>

          {p.wholesale_price && p.wholesale_min_qty && (
            <div className="ds-card mt-3 flex items-center gap-3 border-gold/40 bg-gold-light/60 p-3">
              <Layers className="h-5 w-5 shrink-0 text-gold-dark" />
              <p className="font-num text-sm text-gold-dark">
                Dès {p.wholesale_min_qty} pièces : {fmtGNF(p.wholesale_price)} / unité
              </p>
            </div>
          )}

          <div className="mt-4 flex flex-wrap items-center gap-2">
            {p.stock > 0 ? (
              <span className="ds-chip border-emerald-300 bg-emerald-50 text-emerald-800" data-testid="product-stock-badge">
                En stock · {p.stock} unités
              </span>
            ) : (
              <span className="ds-chip border-slate-300 bg-slate-100 text-slate-600" data-testid="product-stock-badge">
                Rupture de stock
              </span>
            )}
            {low && (
              <span className="ds-chip border-amber-300 bg-amber-50 text-amber-800">
                <AlertTriangle className="h-3.5 w-3.5" /> Stock faible
              </span>
            )}
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <div className="flex items-center rounded-xl border border-slate-300">
              <button
                type="button"
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                className="grid h-11 w-11 place-items-center text-slate-600 hover:bg-slate-50"
                data-testid="qty-decrement"
              >
                <Minus className="h-4 w-4" />
              </button>
              <input
                type="number"
                min={1}
                max={p.stock || 1}
                value={qty}
                onChange={(e) => setQty(Math.max(1, Math.min(Number(e.target.value) || 1, p.stock || 1)))}
                className="font-num h-11 w-16 border-x border-slate-300 text-center text-sm outline-none"
                data-testid="qty-input"
              />
              <button
                type="button"
                onClick={() => setQty((q) => Math.min(q + 1, p.stock || 1))}
                className="grid h-11 w-11 place-items-center text-slate-600 hover:bg-slate-50"
                data-testid="qty-increment"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
            <p className="font-num text-sm text-slate-600">
              Total : <span className="font-bold text-slate-900">{fmtGNF(unit * qty)}</span>
            </p>
          </div>

          <div className="mt-4 flex flex-col gap-2 sm:flex-row">
            <button
              type="button"
              disabled={p.stock <= 0}
              onClick={() => add(false)}
              className="ds-btn-ghost flex-1"
              data-testid="detail-add-to-cart"
            >
              <ShoppingBasket className="h-4 w-4" /> Ajouter au panier
            </button>
            <button
              type="button"
              disabled={p.stock <= 0}
              onClick={() => add(true)}
              className="ds-btn-primary flex-1"
              data-testid="detail-buy-now"
            >
              Commander maintenant
            </button>
          </div>

          <div className="mt-5 grid gap-2 text-sm text-slate-600">
            <p className="flex items-center gap-2"><Truck className="h-4 w-4 text-brand" /> Livraison Entrepôt, Coyah, Dubréka et régions</p>
            <p className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-brand" /> Paiement avant livraison : Orange Money, MTN ou virement</p>
            <p className="flex items-center gap-2"><RotateCcw className="h-4 w-4 text-brand" /> Retour possible sous 48 h après la livraison si l'article est en bon état</p>
          </div>

          <div className="mt-6">
            <h2 className="font-display text-lg font-bold text-slate-900">Description</h2>
            <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-slate-600">{p.description || "—"}</p>
          </div>
        </div>

        <aside className="lg:sticky lg:top-40 lg:self-start" data-testid="product-buy-box">
          <div className="ds-card p-4">
            <p className="font-num text-2xl font-black text-brand">{fmtGNF(unit)}</p>
            {p.wholesale_price && p.wholesale_min_qty && (
              <p className="font-num mt-1 text-xs font-semibold text-gold-dark">
                Dès {p.wholesale_min_qty} pcs : {fmtGNF(p.wholesale_price)} / unité
              </p>
            )}
            <p className="mt-3 text-sm font-semibold text-emerald-700">
              {p.stock > 0 ? "En stock à l'entrepôt" : "Indisponible"}
            </p>
            <div className="mt-3 space-y-1.5 text-xs text-slate-600">
              <p className="flex items-center gap-1.5"><Truck className="h-3.5 w-3.5 text-brand" /> Livraison 10 000 GNF · offerte dès 100 000 GNF</p>
              <p className="flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-brand" /> Retrait à l'entrepôt gratuit</p>
              <p className="flex items-center gap-1.5"><RotateCcw className="h-3.5 w-3.5 text-brand" /> Retour sous 48 h si bon état</p>
            </div>

            <div className="mt-4 flex items-center justify-between gap-2">
              <span className="ds-label">Quantité</span>
              <div className="flex items-center rounded-xl border border-slate-300">
                <button
                  type="button"
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  className="grid h-10 w-10 place-items-center text-slate-600 hover:bg-slate-50"
                  data-testid="buybox-qty-decrement"
                >
                  <Minus className="h-4 w-4" />
                </button>
                <span className="font-num w-10 text-center text-sm">{qty}</span>
                <button
                  type="button"
                  onClick={() => setQty((q) => Math.min(q + 1, p.stock || 1))}
                  className="grid h-10 w-10 place-items-center text-slate-600 hover:bg-slate-50"
                  data-testid="buybox-qty-increment"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            </div>
            <p className="font-num mt-2 text-right text-sm text-slate-600">
              Total : <span className="font-bold text-slate-900">{fmtGNF(unit * qty)}</span>
            </p>

            <button
              type="button"
              disabled={p.stock <= 0}
              onClick={() => add(false)}
              className="ds-btn-gold mt-4 w-full"
              data-testid="buybox-add-to-cart"
            >
              <ShoppingBasket className="h-4 w-4" /> Ajouter au panier
            </button>
            <button
              type="button"
              disabled={p.stock <= 0}
              onClick={() => add(true)}
              className="ds-btn-primary mt-2 w-full"
              data-testid="buybox-buy-now"
            >
              Acheter maintenant
            </button>
          </div>
        </aside>
      </div>

      <ProductReviews productId={p.id} />

      {data.related?.length > 0 && (
        <section className="mt-10">
          <h2 className="mb-4 font-display text-xl font-bold text-slate-900">Produits similaires</h2>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 sm:gap-5">
            {data.related.map((r) => (
              <ProductCard
                key={r.id}
                product={r}
                onAdd={(prod) => {
                  addItem(prod, 1);
                  toast.success(`${prod.name} ajouté au panier`);
                }}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
